import { useQuery } from '@tanstack/react-query'
import { QrCode, Search } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { ProductSummary } from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  InlineAlert,
  SearchInput,
  Spinner,
  cn,
} from '@aeris/ui'

import { fetchProducts } from '../lib/api/catalog.ts'
import {
  inStock,
  money,
  namedColours,
  productLine,
} from '../lib/inbox-commerce.ts'
import { useDebounced } from '../lib/use-debounced.ts'

import { QuickRepliesMenu } from './inbox-quick-replies.tsx'
import { PixChargeDialog } from './pix-charge-dialog.tsx'

/**
 * Vender pelo chat — 27/09.
 *
 * O dono: no atendimento, um botão "Gerar Pix" (digita o valor) e um
 * "Pesquisar produto" (digita "TELA A10"). O que sai dos dois vai para o
 * RASCUNHO da mensagem, e não direto para o cliente: o vendedor lê, ajusta e
 * manda. Nada sai da loja sem alguém apertar Enviar.
 */

export const InboxCommerceActions = ({
  conversationId,
  onInsert,
  branchId,
  disabled,
  quickReplies,
}: {
  conversationId: string | null
  /** Acrescenta ao rascunho. */
  onInsert: (text: string) => void
  branchId: string | undefined
  disabled: boolean
  /**
   * Os atalhos da vendedora (F297): um clique manda ao cliente. Diferente do
   * Pix e do produto, que vão para o rascunho — o atalho é texto que ELA
   * escreveu e já revisou ao criar.
   */
  quickReplies?:
    | {
        readonly customerName: string | null
        readonly onSend: (text: string) => void
      }
    | undefined
}): ReactNode => {
  const [open, setOpen] = useState<'pix' | 'product' | null>(null)

  return (
    <div className="flex gap-1 border-t border-(--border-default) bg-(--surface) px-3 pt-2">
      {quickReplies === undefined ? null : (
        <QuickRepliesMenu
          customerName={quickReplies.customerName}
          disabled={disabled}
          onSend={quickReplies.onSend}
        />
      )}
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        leadingIcon={<QrCode aria-hidden="true" className="h-4 w-4" />}
        onClick={() => {
          setOpen('pix')
        }}
      >
        Gerar Pix
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled}
        leadingIcon={<Search aria-hidden="true" className="h-4 w-4" />}
        onClick={() => {
          setOpen('product')
        }}
      >
        Pesquisar produto
      </Button>

      {open === 'pix' ? (
        <PixChargeDialog
          conversationId={conversationId}
          onClose={() => {
            setOpen(null)
          }}
          onInsert={(text) => {
            onInsert(text)
            setOpen(null)
          }}
        />
      ) : null}
      {open === 'product' ? (
        <ProductSearchDialog
          branchId={branchId}
          onClose={() => {
            setOpen(null)
          }}
          onInsert={(text) => {
            onInsert(text)
            setOpen(null)
          }}
        />
      ) : null}
    </div>
  )
}

/** Uma busca traz até isto; passou, a tela pede para refinar. */
const PAGE_SIZE = 50

/** "3.000000" vira "3": o estoque vem com seis casas, e a tela é do balcão. */
const quantity = (value: string): string =>
  value.includes('.') ? value.replace(/\.?0+$/, '') : value

const ProductSearchDialog = ({
  branchId,
  onClose,
  onInsert,
}: {
  branchId: string | undefined
  onClose: () => void
  onInsert: (text: string) => void
}): ReactNode => {
  const [search, setSearch] = useState('')
  const term = useDebounced(search).trim()
  /*
   * Tudo o que a busca achou já vai marcado (F208 — o dono: "não precisa
   * selecionar, já vai tudo"). O estado guarda só o que foi DESMARCADO, e só
   * para o termo em que foi desmarcado: buscar outra coisa volta a marcar tudo.
   */
  const [removed, setRemoved] = useState<{
    readonly term: string
    readonly ids: ReadonlySet<string>
  }>({ term: '', ids: new Set() })
  const removedIds = removed.term === term ? removed.ids : new Set<string>()

  const results = useQuery({
    queryKey: ['inbox-product-search', term, branchId ?? ''],
    queryFn: async ({ signal }) =>
      await fetchProducts(
        {
          search: term,
          status: 'active',
          page: 1,
          pageSize: PAGE_SIZE,
          // F297: as cores, com o estoque de cada uma na filial.
          include:
            branchId === undefined
              ? ['price', 'colours']
              : ['price', 'stock', 'colours'],
          ...(branchId === undefined ? {} : { branchId }),
        },
        signal
      ),
    enabled: term.length > 1,
  })

  const found = term.length > 1 ? (results.data?.items ?? []) : []
  /** Na ordem da busca — é a ordem da mensagem, a mesma do robô. */
  const picked = found.filter((product) => !removedIds.has(product.id))
  const allPicked = found.length > 0 && picked.length === found.length

  const toggle = (product: ProductSummary): void => {
    const next = new Set(removedIds)
    if (next.has(product.id)) next.delete(product.id)
    else next.add(product.id)
    setRemoved({ term, ids: next })
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Pesquisar produto"
        description="Tudo o que a busca achar já vai marcado, com o preço e se tem pronta entrega. Desmarque o que não quiser."
      >
        <div className="flex flex-col gap-3">
          <SearchInput
            aria-label="Buscar produto"
            placeholder="Ex.: TELA A10"
            value={search}
            autoFocus
            onChange={(event) => {
              setSearch(event.target.value)
            }}
          />

          <div className="max-h-[60vh] overflow-y-auto">
            {term.length < 2 ? (
              <p className="py-6 text-center text-(length:--text-sm) text-(--text-muted)">
                Digite ao menos duas letras.
              </p>
            ) : results.isPending ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : results.isError ? (
              <InlineAlert tone="danger">
                Não foi possível buscar agora.
              </InlineAlert>
            ) : results.data.items.length === 0 ? (
              <p className="py-6 text-center text-(length:--text-sm) text-(--text-muted)">
                Nenhum produto para "{term}".
              </p>
            ) : (
              <ul className="flex flex-col gap-1" aria-label="Produtos">
                {results.data.items.map((product) => {
                  const stock = inStock(product)
                  const chosen = !removedIds.has(product.id)
                  return (
                    <li key={product.id}>
                      <button
                        type="button"
                        aria-pressed={chosen}
                        onClick={() => {
                          toggle(product)
                        }}
                        className={cn(
                          'flex w-full items-center justify-between gap-3 rounded-(--radius-md) border px-3 py-2 text-left',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)',
                          chosen
                            ? 'border-(--brand-primary) bg-(--surface-sunken)'
                            : 'border-(--border-subtle) hover:bg-(--surface-hover)'
                        )}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-(--weight-semibold)">
                            {product.name}
                          </span>
                          <span className="text-(length:--text-xs) text-(--text-muted)">
                            {product.variants[0]?.sku ?? ''}
                            {/*
                              F217: sem estoque em vermelho — o atendente vê
                              antes de oferecer. Quem não controla estoque não
                              diz nada, em vez de um "0" que não é verdade.
                            */}
                            {stock === false ? (
                              <>
                                {' · '}
                                <span className="font-(--weight-semibold) text-(--danger)">
                                  Sem estoque
                                </span>
                              </>
                            ) : stock === true &&
                              product.stock?.available !== undefined ? (
                              ` · ${quantity(product.stock.available)} em estoque`
                            ) : (
                              ''
                            )}
                          </span>
                          {/*
                            As cores (F297): "Preto 4 · Branco 0". A sem
                            estoque fica apagada — o atendente vê qual
                            oferecer.
                          */}
                          {namedColours(product).length === 0 ? null : (
                            <span
                              aria-label="Cores"
                              className="flex flex-wrap gap-x-2 text-(length:--text-xs)"
                            >
                              {namedColours(product).map((colour) => (
                                <span
                                  key={colour.sku}
                                  className={cn(
                                    colour.available !== null &&
                                      !/[1-9]/.test(colour.available)
                                      ? 'text-(--text-muted) line-through'
                                      : 'text-(--text-secondary)'
                                  )}
                                >
                                  {colour.name}
                                  {colour.available === null
                                    ? ''
                                    : ` ${quantity(colour.available)}`}
                                </span>
                              ))}
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            'shrink-0 font-(--font-mono) tabular-nums',
                            stock === false && 'text-(--text-muted)'
                          )}
                        >
                          {money(product.price?.basePrice)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          {found.length === 0 ? null : (
            <div className="flex flex-wrap items-center justify-between gap-2 text-(length:--text-xs) text-(--text-secondary)">
              <span role="status">
                {String(picked.length)} de {String(found.length)} marcados
                {results.data !== undefined && results.data.total > found.length
                  ? ` · mostrando ${String(found.length)} de ${String(results.data.total)}, refine a busca`
                  : ''}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRemoved({
                    term,
                    ids: allPicked
                      ? new Set(found.map((product) => product.id))
                      : new Set(),
                  })
                }}
              >
                {allPicked ? 'Desmarcar todos' : 'Marcar todos'}
              </Button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={picked.length === 0}
            onClick={() => {
              onInsert(picked.map(productLine).join('\n'))
            }}
          >
            {picked.length <= 1
              ? 'Pôr na mensagem'
              : allPicked
                ? `Pôr os ${String(picked.length)} na mensagem`
                : `Pôr ${String(picked.length)} na mensagem`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
